// apps/frontend/src/lib/lazyWithRetry.ts
// Drop-in replacement de React.lazy qui gère le cas "chunk obsolète après
// déploiement". Vite produit des chunks avec hash dans le nom, et Railway
// supprime les anciens à chaque deploy. Si un user a un onglet ouvert avant
// un deploy puis essaie de naviguer vers une route lazy après, le browser
// fetch un chunk qui n'existe plus → "Failed to fetch dynamically imported
// module" → page d'erreur.
//
// Stratégie : on n'oblige PAS un reload brutal (ça ferait perdre la saisie
// en cours). À la place on émet un CustomEvent 'glowp:update-available'
// que le composant <AppUpdateNotice /> capte pour afficher une notif Glowp
// avec bouton "Recharger". Si l'user n'agit pas dans les 60s on relance
// la notif (la nouvelle version reste disponible).
//
// Tant que l'user n'a pas reload, le composant lazy reste en attente
// (Suspense fallback). Pas d'écran d'erreur, pas de perte de contexte.
//
// Usage : remplacer `import { lazy } from 'react'` par
// `import { lazy } from './lib/lazyWithRetry'`. Aucun autre changement.

import { lazy as reactLazy, ComponentType, LazyExoticComponent } from 'react';

const NOTIFY_GUARD_KEY = 'glowp_update_notified_at';
const NOTIFY_GUARD_MS = 60_000;
export const UPDATE_AVAILABLE_EVENT = 'glowp:update-available';

function isChunkLoadError(err: unknown): boolean {
  const msg =
    err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('ChunkLoadError')
  );
}

function shouldNotify(): boolean {
  try {
    const last = sessionStorage.getItem(NOTIFY_GUARD_KEY);
    const now = Date.now();
    if (!last || now - parseInt(last, 10) > NOTIFY_GUARD_MS) {
      sessionStorage.setItem(NOTIFY_GUARD_KEY, String(now));
      return true;
    }
  } catch {
    return true;
  }
  return false;
}

function emitUpdateAvailable() {
  if (typeof window === 'undefined') return;
  if (!shouldNotify()) return;
  window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT));
}

export function lazy<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return reactLazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err)) {
        // Notifie l'user via banner Glowp ; on ne resolve PAS la promesse
        // pour ne pas remonter l'erreur vers le ErrorBoundary, ce qui garde
        // l'écran courant intact (Suspense fallback). L'user clique
        // "Recharger" sur la banner quand il est prêt.
        emitUpdateAvailable();
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
